import { Controller } from "@hotwired/stimulus"

// Bid calculator wizard — companion to the generic v2-stepper controller.
// Handles domain-specific logic: ad account selection, campaign/CA loading
// via Turbo Frames, validation, and summaries.
// Date range selection is delegated to the shared date-range-picker controller.
export default class extends Controller {
  static targets = [
    "adAccountRadio",
    "campaignCheckbox",
    "conversionActionCheckbox",
    "bufferInput",
  ]

  static outlets = ["v2-stepper"]

  // --- Date changed (from shared date-range-picker component) ---

  dateChanged(event) {
    this._dateFrom = event.detail.from
    this._dateTo = event.detail.to
  }

  // --- Validation (called via v2-stepper:validate event) ---

  validate(event) {
    const step = event.detail.step
    let valid = true

    switch (step) {
      case 1: {
        // Check the shared date-range-picker's hidden fields
        const form = this.element.closest("form")
        const dateFrom = form?.querySelector('input[name="bid_calculator[date_start]"]')
        const dateTo = form?.querySelector('input[name="bid_calculator[date_end]"]')
        valid = !!(dateFrom?.value && dateTo?.value)
        break
      }
      case 2:
        valid = this.adAccountRadioTargets.some((r) => r.checked)
        break
      case 3:
        valid = this.campaignCheckboxTargets.some((cb) => cb.checked)
        break
      case 4:
        valid = this.conversionActionCheckboxTargets.some((cb) => cb.checked)
        break
      case 5:
        valid = true
        break
    }

    if (!valid) event.preventDefault()
  }

  // --- Update summaries when stepper advances ---

  stepChanged(event) {
    const stepper = this.v2StepperOutlet
    if (!stepper) return

    for (let i = 1; i < event.detail.step; i++) {
      stepper.setSummary(i, this.summaryForStep(i))
    }
  }

  summaryForStep(step) {
    switch (step) {
      case 1: {
        const form = this.element.closest("form")
        const start = form?.querySelector('input[name="bid_calculator[date_start]"]')?.value
        const end = form?.querySelector('input[name="bid_calculator[date_end]"]')?.value
        if (start && end) {
          const days = this.daysBetween(start, end)
          return `${start} to ${end} (${days} days)`
        }
        return ""
      }
      case 2: {
        const selected = this.adAccountRadioTargets.find((r) => r.checked)
        if (selected) {
          const label = selected
            .closest("label")
            ?.querySelector("[data-account-name]")
          return label ? label.textContent.trim() : selected.value
        }
        return ""
      }
      case 3: {
        const count = this.campaignCheckboxTargets.filter(
          (cb) => cb.checked,
        ).length
        return `${count} campaign${count !== 1 ? "s" : ""} selected`
      }
      case 4: {
        const count = this.conversionActionCheckboxTargets.filter(
          (cb) => cb.checked,
        ).length
        return `${count} conversion action${count !== 1 ? "s" : ""} selected`
      }
      case 5: {
        const buffer = this.hasBufferInputTarget
          ? this.bufferInputTarget.value || "0"
          : "0"
        return `Buffer: ${buffer}%`
      }
      default:
        return ""
    }
  }

  // --- Ad Account (Step 2) ---

  adAccountSelected(event) {
    const adAccountId = event.currentTarget.value
    const dateParams = this._dateQueryString()

    const campaignsFrame = document.getElementById("wizard-campaigns-frame")
    if (campaignsFrame && campaignsFrame.dataset.baseUrl) {
      campaignsFrame.src =
        campaignsFrame.dataset.baseUrl +
        "?ad_account_id=" +
        adAccountId +
        dateParams
    }

    const casFrame = document.getElementById(
      "wizard-conversion-actions-frame",
    )
    if (casFrame && casFrame.dataset.baseUrl) {
      casFrame.src =
        casFrame.dataset.baseUrl +
        "?ad_account_id=" +
        adAccountId +
        dateParams
    }
  }

  // --- Helpers ---

  daysBetween(start, end) {
    const a = new Date(start)
    const b = new Date(end)
    return Math.round((b - a) / (1000 * 60 * 60 * 24))
  }

  _dateQueryString() {
    const form = this.element.closest("form")
    const dateFrom = form?.querySelector(
      'input[name="bid_calculator[date_start]"]',
    )?.value
    const dateTo = form?.querySelector(
      'input[name="bid_calculator[date_end]"]',
    )?.value
    if (dateFrom && dateTo) {
      return `&date_start=${dateFrom}&date_end=${dateTo}`
    }
    return ""
  }
}
